import { useState, useEffect } from 'react';
import { useOnlineStatus } from './use-online-status';

type ConnectionQuality = 'strong' | 'medium' | 'weak' | 'offline';

/**
 * useConnectionQuality - Detects network quality via Network Information API
 * Returns 'strong', 'medium', 'weak', or 'offline'
 */
export const useConnectionQuality = () => {
  const { isOnline } = useOnlineStatus();
  const [quality, setQuality] = useState<ConnectionQuality>('strong');

  useEffect(() => {
    if (!isOnline) {
      setQuality('offline');
      return;
    }

    const connection = (navigator as any).connection;
    
    // Navegadores sem Network API (iOS Safari) - assume conexão boa
    if (!connection) {
      setQuality('strong');
      return;
    }

    const updateQuality = () => {
      const { effectiveType, rtt } = connection;

      // 4G com latência boa/aceitável ou sem RTT disponível = strong
      if (effectiveType === '4g' && (rtt === undefined || rtt < 300)) {
        setQuality('strong');
        return;
      }

      // 4G com latência alta = medium
      if (effectiveType === '4g' && rtt < 500) {
        setQuality('medium');
        return;
      }

      // 3G com latência ok = medium
      if (effectiveType === '3g' && rtt < 200) {
        setQuality('medium');
        return;
      }

      // 2G, slow-2g, ou latência muito alta = weak
      setQuality('weak');
    };

    updateQuality();
    connection.addEventListener('change', updateQuality);
    return () => connection.removeEventListener('change', updateQuality);
  }, [isOnline]);

  return { quality, isOnline };
};
